// A missing amount shows "—"; only a real 0 shows ₦0.
// Number("") and Number("  ") are 0, so blank strings must count as missing.
export const formatNaira = (value) => {
  const isNumber = typeof value === "number";
  const isNumericText = typeof value === "string" && value.trim() !== "";
  if (!isNumber && !isNumericText) return "—";
  const amount = Number(value);
  return Number.isFinite(amount) ? `₦${amount.toLocaleString()}` : "—";
};

// Kobo to naira, keeping the kobo when there are any: 125050 -> "₦1,250.50".
export const formatKobo = (kobo) => {
  if (typeof kobo !== "number" || !Number.isFinite(kobo)) return "—";
  const naira = kobo / 100;
  const hasKobo = kobo % 100 !== 0;
  return `₦${naira.toLocaleString("en-NG", {
    minimumFractionDigits: hasKobo ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};

// Typed naira to whole kobo, with string maths so 0.29 never becomes 28.999…:
// "1,500.50" -> { kobo: 150050 }. Commas must be thousands groups.
export const parseNairaToKobo = (input) => {
  const text = String(input ?? "").trim().replace(/^₦\s*/, "");
  if (text === "") return { error: "Enter an amount." };
  if (text.startsWith("-")) return { error: "Leave out the minus sign. Choose Debit to take money out." };
  const match = /^(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?$/.exec(text);
  if (!match) return { error: "Enter a number like 1,500 or 1,500.50." };
  const [, whole, fraction = ""] = match;
  if (fraction.length > 2) return { error: "Use at most 2 digits after the point." };
  const kobo = Number(whole.replace(/,/g, "")) * 100 + Number(fraction.padEnd(2, "0"));
  if (!Number.isSafeInteger(kobo)) return { error: "That amount is too large." };
  if (kobo === 0) return { error: "Enter an amount above ₦0." };
  return { kobo };
};

// The server refuses a single wallet adjustment above ₦100,000; exactly ₦100,000 is allowed.
export const MAX_ADJUSTMENT_KOBO = 100_000 * 100;
export const MAX_ADJUSTMENT_MESSAGE = "A single adjustment can't be more than ₦100,000.";

// One idempotency key per intended adjustment, held by the page so closing and
// reopening the modal reuses it: the server applies a key at most once.
export const adjustmentIntent = (userId, direction, kobo) => `${userId}:${direction}:${kobo}`;

export const adjustmentKeyFor = (keys, intent, makeKey = () => crypto.randomUUID()) => {
  if (!keys.has(intent)) keys.set(intent, makeKey());
  return keys.get(intent);
};

// What to do after a send. Only a 2xx proves it applied, so only then is the key dropped.
// No response or a 5xx may still have applied it: keep the key, reload the balance, allow a retry.
export const adjustmentFollowUp = (status) => {
  const applied = status >= 200 && status < 300;
  const maybeApplied = !status || status >= 500;
  return { applied, clearKey: applied, reloadBalance: applied || maybeApplied, canRetry: maybeApplied };
};
